import { inject, Injectable } from '@angular/core';
import { User } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  FeasibilityAudience,
  FeasibilityComputation,
  FeasibilityCriterion,
  FeasibilityQuota,
  Survey,
} from '@ps/survey-utils';
import { map, Observable, tap } from 'rxjs';

import { FeasibilityApi } from '../../api/feasibility/feasibility.api';
import { FeasibilityPayloadMapper } from '../../api/feasibility/mappers/feasibility.payload.mapper';

import { FeasibilityAnalysis } from './domain/feasibility-analysis';
import { QuotaRowMapper } from './domain/quota-row.mapper';
import { formatFeasibilityCondition } from './domain/feasibility-condition.formatter';

@Injectable({ providedIn: 'root' })
export class FeasibilityService {
  private readonly _api = inject(FeasibilityApi);
  private readonly _toast = inject(ToastService);

  private readonly _payloadMapper = new FeasibilityPayloadMapper();

  public compute(
    survey: Survey,
    user: User,
  ): Observable<FeasibilityComputation> {
    const { request, defaultQuotaCriteria, quotaSet } =
      this._payloadMapper.build(survey, user.id);

    return this._api.getFeasibility(request).pipe(
      map((result) => FeasibilityAnalysis.from(result)),
      tap((analysis) => {
        this._notifyUnusedQualifications(
          analysis,
          quotaSet,
          survey,
          user.email,
        );
      }),
      map((analysis) => {
        return {
          total: analysis.total,
          audience: this._buildFeasibilityAudience(
            defaultQuotaCriteria,
            survey.currencySymbol,
          ),
          quotaGroups: new QuotaRowMapper(analysis, survey.currencySymbol).map(
            quotaSet,
          ),
        };
      }),
    );
  }

  /**
   * Warns once per computation when a qualification was excluded, then pings
   * internal support unless the buyer entered an invalid range (from > to)
   */
  private _notifyUnusedQualifications(
    analysis: FeasibilityAnalysis,
    quotaSet: ReadonlyMap<number | string, readonly FeasibilityQuota[]>,
    survey: Survey,
    userEmail: string,
  ): void {
    const rows = [...quotaSet.values()].flat();
    const unusedNames = analysis.unusedQualificationNames(rows);

    if (!unusedNames.length) {
      return;
    }

    this._toast.warning(
      `The feasibility calculation could not use: ${unusedNames.join(', ')}`,
    );

    if (this._hasInvalidRange(rows)) {
      return;
    }

    const unusedCodes = [
      ...new Set(
        rows
          .filter((row) => analysis.isUnknown(row.qualificationCode, row))
          .map((row) => row.qualificationCode),
      ),
    ];

    this._api
      .notifyUnusedQualification({
        projectId: survey.id,
        user: userEmail,
        criteria: unusedNames.join(', '),
        qualifications: unusedCodes.join(', '),
        localization: `${survey.languageCode}_${survey.countryCode}`,
      })
      .subscribe();
  }

  private _hasInvalidRange(rows: readonly FeasibilityCriterion[]): boolean {
    return rows.some((row) => {
      const range = row.rangeSets?.[0];
      return !!range && range.from > range.to;
    });
  }

  private _buildFeasibilityAudience(
    defaultQuotaCriteria: readonly FeasibilityCriterion[],
    currency: string,
  ): FeasibilityAudience[] {
    return defaultQuotaCriteria.map((criterion) => ({
      label: criterion.qualificationName ?? '',
      value: formatFeasibilityCondition(criterion, currency),
    }));
  }
}
